import { Camera, Image /* Video */ } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Background from "../components/background.tsx";
import DashboardContent from "../components/dashboard/dashboard-content.tsx";
import Header from "../components/header.tsx";
import Sidebar from "../components/sidebar.tsx";
import TechnoArtLogo from "../components/technoArtLogo.tsx";

interface TabOption {
  icon: React.ComponentType<any>;
  label: string;
  active: boolean;
}

interface DetectionResult {
  bbox: number[];
  confidence: number;
  class_id: number;
  class_name: string;
  plate_text?: string;
  ocr_confidence?: number;
  vehicle_type?: string;
  region?: string;
}

interface ProcessResponse {
  filename: string;
  vehicle_image: string;
  plate_image: string;
  detections: {
    vehicles: DetectionResult[];
    plates: DetectionResult[];
  };
  detection_ids: number[];
  processing_time: number;
}

const Dashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<
    "image" | /* "video" | */ "webcam"
  >("image");
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const navigate = useNavigate();
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);

  // New states for API integration
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [processResult, setProcessResult] = useState<ProcessResponse | null>(
    null
  );
  const [processError, setProcessError] = useState<string | null>(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const webcamStreamRef = useRef<MediaStream | null>(null);

  const [cameraDevices, setCameraDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedCameraId, setSelectedCameraId] = useState<string | undefined>(
    undefined
  );

  const updateImageStates = (dataUrl: string) => {
    setSelectedImage(dataUrl);
    setUploadedImage(dataUrl);
  };

  // Fetch camera devices on mount
  useEffect(() => {
    const getCameras = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter((d) => d.kind === "videoinput");
        setCameraDevices(videoDevices);
        if (videoDevices.length > 0) {
          setSelectedCameraId(videoDevices[0].deviceId);
        }
      } catch (err) {
        console.error("Error fetching camera devices:", err);
      }
    };
    getCameras();
  }, []);

  const stopWebcam = () => {
    if (webcamStreamRef.current) {
      webcamStreamRef.current.getTracks().forEach((track) => track.stop());
      webcamStreamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  useEffect(() => {
    const enableWebcam = async () => {
      try {
        const constraints: MediaStreamConstraints = {
          video: selectedCameraId
            ? { deviceId: { exact: selectedCameraId } }
            : true,
        };
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        webcamStreamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
      } catch (err) {
        alert("Could not access webcam. Please allow camera access.");
        console.error("Webcam error:", err);
      }
    };

    if (activeTab === "webcam") {
      enableWebcam();
    } else {
      stopWebcam();
      updateImageStates("");
    }

    return () => {
      stopWebcam();
    };
  }, [activeTab, selectedCameraId]); // Add selectedCameraId as dependency

  const tabOptions: TabOption[] = [
    { icon: Image, label: "Image", active: activeTab === "image" },
    // { icon: Video, label: "Video", active: activeTab === "video" },
    { icon: Camera, label: "Webcam", active: activeTab === "webcam" },
  ];

  const handleTabClick = (tab: "image" | /* "video" | */ "webcam") => {
    setActiveTab(tab);
    updateImageStates("");
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result as string;
        updateImageStates(dataUrl);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleReset = async () => {
    updateImageStates("");
    if (activeTab === "webcam") {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
        });
        webcamStreamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
      } catch (err) {
        console.error("Error reinitializing webcam:", err);
      }
    }
  };

  // Helper: Crop image inside green box from a video element and return a data URL
  function cropImageToGreenBox(video: HTMLVideoElement): Promise<string> {
    return new Promise((resolve) => {
      const width = video.videoWidth;
      const height = video.videoHeight;
      const points = GREEN_BOX_POINTS.map((p) => ({
        x: p.x * width,
        y: p.y * height,
      }));

      const minX = Math.min(...points.map((p) => p.x));
      const maxX = Math.max(...points.map((p) => p.x));
      const minY = Math.min(...points.map((p) => p.y));
      const maxY = Math.max(...points.map((p) => p.y));
      const cropWidth = maxX - minX;
      const cropHeight = maxY - minY;

      const canvas = document.createElement("canvas");
      canvas.width = cropWidth;
      canvas.height = cropHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) return resolve("");

      ctx.save();
      ctx.beginPath();
      ctx.moveTo(points[0].x - minX, points[0].y - minY);
      for (let i = 1; i < points.length; i++) {
        ctx.lineTo(points[i].x - minX, points[i].y - minY);
      }
      ctx.closePath();
      ctx.clip();
      ctx.drawImage(
        video,
        minX,
        minY,
        cropWidth,
        cropHeight,
        0,
        0,
        cropWidth,
        cropHeight
      );
      ctx.restore();

      resolve(canvas.toDataURL("image/png"));
    });
  }

  // Accept showGreenbox as a parameter
  const captureFromWebcam = async (showGreenbox: boolean) => {
    const video = videoRef.current;
    if (!video) return;

    if (video.readyState >= 2) {
      const tempCanvas = document.createElement("canvas");
      tempCanvas.width = video.videoWidth;
      tempCanvas.height = video.videoHeight;
      const tempCtx = tempCanvas.getContext("2d");
      if (tempCtx) {
        tempCtx.drawImage(video, 0, 0, tempCanvas.width, tempCanvas.height);
        const originalDataUrl = tempCanvas.toDataURL("image/png");
        setUploadedImage(originalDataUrl);

        if (showGreenbox) {
          const croppedDataUrl = await cropImageToGreenBox(video);
          setSelectedImage(croppedDataUrl);
        } else {
          setSelectedImage(originalDataUrl);
        }
      }
      stopWebcam();
    } else {
      alert("Camera not ready yet. Please wait a second and try again.");
    }
  };

  const handleProcessDetection = async () => {
    // If webcam tab, download the crop image before processing
    if (activeTab === "webcam" && selectedImage) {
      // Download the cropped image
      const a = document.createElement("a");
      a.href = selectedImage;
      a.download = "crop-output.png";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Continue to process detection as before
    }

    // Use uploadedImage (original) for detection and result page
    const imageToProcess = uploadedImage;

    if (!imageToProcess) {
      alert("Please upload an image or capture a photo first.");
      return;
    }

    setIsProcessing(true);
    setProcessError(null);
    setProcessResult(null);

    try {
      // Convert base64 to file
      const base64Response = await fetch(imageToProcess);
      const blob = await base64Response.blob();
      const file = new File([blob], "uploaded-image.png", {
        type: "image/png",
      });

      // Create form data
      const formData = new FormData();
      formData.append("file", file);

      const apiUrl = "https://galaxy-alpr-be.onrender.com/detect/image";
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      const response = await fetch(apiUrl, {
        method: "POST",
        body: formData,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `API error: ${response.status} ${response.statusText}. ${errorText}`
        );
      }

      const result = await response.json();

      if (result.error) {
        throw new Error(result.error);
      }

      setProcessResult(result);
      setShowSuccessMessage(true);

      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 5000);

      // if (result && result.vehicle_image && result.plate_image) {
      //   navigate("/result", {
      //     state: {
      //       originalImage: imageToProcess, // <-- send original image
      //       detectedImage: result.plate_image,
      //       detectionResult: result,
      //     },
      //   });
      // }
    } catch (error) {
      if (error instanceof TypeError && error.message === "Failed to fetch") {
        setProcessError(
          "Network error: Failed to connect to the API server. Make sure the backend is running at https://galaxy-alpr-be.onrender.com"
        );
      } else {
        setProcessError(
          error instanceof Error ? error.message : "Unknown error occurred"
        );
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const isActionDisabled = !selectedImage;

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background */}
      <Background />

      {/* Success message overlay */}
      {showSuccessMessage && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md">
            <h3 className="text-xl font-bold text-green-600 mb-3">
              Detection Processed Successfully!
            </h3>
            <p className="mb-4">
              Your image has been processed and saved to the detection history.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowSuccessMessage(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setShowSuccessMessage(false);
                  navigate("/history", {
                    state: {
                      originalImage: uploadedImage,
                      detectedImage: processResult?.vehicle_image,
                      detectionResult: processResult,
                    },
                  });
                }}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
              >
                View History
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex relative z-10">
        {/* Sidebar */}
        <Sidebar />

        {/* Main Content */}
        <div className="flex-1">
          {/* Header */}
          <Header />

          {/* Content Area */}
          <DashboardContent
            tabOptions={tabOptions}
            activeTab={activeTab}
            handleTabClick={handleTabClick}
            isDragging={isDragging}
            setIsDragging={setIsDragging}
            selectedImage={selectedImage}
            updateImageStates={updateImageStates}
            handleFileUpload={handleFileUpload}
            videoRef={videoRef}
            canvasRef={canvasRef}
            handleReset={handleReset}
            captureFromWebcam={captureFromWebcam}
            handleProcessDetection={handleProcessDetection}
            isActionDisabled={isActionDisabled}
            isProcessing={isProcessing}
            processResult={processResult}
            processError={processError}
            cameraDevices={cameraDevices}
            selectedCameraId={selectedCameraId}
            setSelectedCameraId={setSelectedCameraId}
          />

          {/* Footer */}
          <div className="text-center py-4">
            <p className="text-gray-500 text-opacity-80 text-sm">
              &copy;2025, Galaxy Dev
            </p>
          </div>
        </div>
      </div>
      <TechnoArtLogo />
    </div>
  );
};

export default Dashboard;

// These points are normalized to a 600x400 image size
// will replace with the actual points for the green box from db
// { x: x1 / 600, y: y1 / 400 },
// { x: x2 / 600, y: y2 / 400 },
// { x: x3 / 600, y: y3 / 400 },
// { x: x4 / 600, y: y4 / 400 },
export const GREEN_BOX_POINTS = [
  { x: 150 / 600, y: 200 / 400 },
  { x: 450 / 600, y: 280 / 400 },
  { x: 500 / 600, y: 350 / 400 },
  { x: 100 / 600, y: 370 / 400 },
];
